import React, { useState } from 'react';
import { MessageSquarePlus, X, Bug, Lightbulb, MessageCircle, Send, Loader2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useNotifications } from '../../context/NotificationContext';

const EDGE_FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-github-issue`;

type FeedbackType = 'bug' | 'feature' | 'other';

const TYPES: { value: FeedbackType; label: string; icon: React.ReactNode; color: string }[] = [
  { value: 'bug', label: 'Bug', icon: <Bug size={16} />, color: 'text-red-400 border-red-500/40 bg-red-500/10' },
  { value: 'feature', label: 'Amélioration', icon: <Lightbulb size={16} />, color: 'text-yellow-400 border-yellow-500/40 bg-yellow-500/10' },
  { value: 'other', label: 'Autre', icon: <MessageCircle size={16} />, color: 'text-blue-400 border-blue-500/40 bg-blue-500/10' },
];

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const { notify } = useNotifications();

  const reset = () => {
    setTitle('');
    setDescription('');
    setType('bug');
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return;

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data: { user } } = await supabase.auth.getUser();

      const res = await fetch(EDGE_FN_URL, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          type,
          title: title.trim(),
          description: description.trim(),
          userEmail: user?.email ?? null,
          currentPage: location.pathname,
        }),
      });

      if (!res.ok) throw new Error('Erreur serveur');

      notify('success', 'Feedback envoyé', { description: 'Merci pour votre retour !' });
      handleClose();
    } catch {
      notify('error', "Échec de l'envoi du feedback", { description: 'Vérifiez votre connexion et réessayez.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium shadow-lg shadow-indigo-500/20 transition-all hover:scale-105"
        aria-label="Envoyer un feedback"
      >
        <MessageSquarePlus size={16} />
        Feedback
      </button>

      {/* Modal overlay */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-end p-6" onClick={handleClose}>
          <div
            className="w-full max-w-sm bg-gray-900 border border-white/10 rounded-2xl shadow-2xl p-5 flex flex-col gap-4"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-white font-semibold text-base">Envoyer un retour</h2>
              <button onClick={handleClose} className="text-gray-400 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              {/* Type selector */}
              <div className="flex gap-2">
                {TYPES.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setType(t.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                      type === t.value ? t.color : 'text-gray-400 border-white/10 bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    {t.icon}
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Title */}
              <input
                type="text"
                placeholder="Titre court"
                value={title}
                onChange={e => setTitle(e.target.value)}
                required
                maxLength={100}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors"
              />

              {/* Description */}
              <textarea
                placeholder="Décrivez le problème ou votre idée..."
                value={description}
                onChange={e => setDescription(e.target.value)}
                required
                rows={4}
                maxLength={1000}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors resize-none"
              />

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || !title.trim() || !description.trim()}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-all"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {loading ? 'Envoi...' : 'Envoyer'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
